import { UseWebSocketProps } from "components/atendimentos-components/use-init-web-socket/types";
import { fileToBase64 } from "utils/file-to-base64";
import { makeId } from "utils/make-id";
import { WhatsAppMessageStore } from "utils/whatsapp-cached-messages";
import { Message } from "../types";

export const whatsappMessageStore = new WhatsAppMessageStore();
whatsappMessageStore.init();

export class WhatsAppMessagesHelper {
  private wsSession: UseWebSocketProps;

  constructor(wsSession: UseWebSocketProps) {
    this.wsSession = wsSession;
  }

  // Sends a text message
  public sendText({ body }: { body: string }) {
    body = body.trim();
    const isValid = !(body === "");
    if (isValid) {
      this.wsSession.sendJsonMessage({
        contentType: "action",
        action: "send-message",
        whatsappNewMessage: { type: "chat", body: body },
      });
      return true;
    }
  }

  // Sends an audio file by splitting it into parts
  public async sendAudio(audio: Blob) {
    this.sendSplitedFile(audio, (dataPart) => ({
      contentType: "action",
      action: "send-voice",
      voiceMedia: dataPart,
    }));
  }

  // Sends an image file by splitting it into parts
  public async sendImage(file: Blob) {
    this.sendSplitedFile(file, (dataPart) => ({
      contentType: "action",
      action: "send-image",
      imageMedia: dataPart,
    }));
  }

  // Sends an image file by splitting it into parts
  public async sendVideo(file: Blob) {
    this.sendSplitedFile(file, (dataPart) => ({
      contentType: "action",
      action: "send-video",
      videoMedia: dataPart,
    }));
  }

  // Sends an image file by splitting it into parts
  public async sendDocument(file: Blob | File) {
    this.sendSplitedFile(file, (dataPart) => ({
      contentType: "action",
      action: "send-document",
      filename: (file as File).name,
      documentMedia: dataPart,
    }));
  }

  // Splits a file into parts and sends each part as a separate message
  private async sendSplitedFile(
    file: Blob,
    mountMessage: (dataPart: FileDataPart) => {
      contentType: string;
      action: string;
      [key: string]: any;
    }
  ) {
    const partSize = 1000;
    const base64FileString = await fileToBase64(file);
    const length = base64FileString.length;
    const amountParts = Math.ceil(length / partSize);
    const hashId = makeId(10);

    for (let i = 0; i < amountParts; i++) {
      const dataPart = base64FileString.substring(
        i * 2000,
        (i + 1) * 2000 < length ? (i + 1) * 2000 : length
      );
      const fileDataPart = { hashId, part: i, amountParts, dataPart };
      const message = mountMessage(fileDataPart);
      this.wsSession.sendJsonMessage(message);
    }
  }

  // Requests media for a given message
  public requestMedia(message: Message) {
    this.wsSession.sendJsonMessage({
      action: "request-media",
      contentType: "action",
      requestMessageId: message.id,
      url: message.url,
      mediaKey: message.mediaKey,
      mimetype: message.mimetype,
      type: message.type,
      directPath: message.directPath,
    });
  }
}

interface FileDataPart {
  hashId: string;
  part: number;
  amountParts: number;
  dataPart: string;
}
